/**
 * Availability-columns data seam for the merged table's availability wiring
 * (offer-unified-table, CU-86cbc3r8b): the show/hide + granularity
 * preference, the unit-scoped full-range fetch, and the derived grids
 * `useOfferTableAvailability`'s column build, multi-select, and
 * INSPECT/queue wiring all read from. Extracted from
 * `use-offer-table-availability.tsx` — see that file's own doc comment.
 *
 * Delegates to `use-offer-availability-query.ts` (preferences/fetch) and
 * `use-offer-availability-grids.ts` (derived occupancy/entries/grid) — each
 * independently under the `.ts` max-lines-per-function budget.
 */
import { useOfferAvailabilityQuery } from './use-offer-availability-query';
import { useOfferAvailabilityGrids } from './use-offer-availability-grids';
import type { OfferUnitItem } from '../units-tab-columns';

export interface UseOfferAvailabilityDataOptions {
  offerId: number;
  rows: OfferUnitItem[];
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
  forceShow: boolean;
}

export function useOfferAvailabilityData({
  offerId,
  rows,
  defaultDateFrom,
  defaultDateTo,
  forceShow,
}: UseOfferAvailabilityDataOptions) {
  const query = useOfferAvailabilityQuery({
    offerId,
    rows,
    defaultDateFrom,
    defaultDateTo,
    forceShow,
  });
  const grids = useOfferAvailabilityGrids({
    rows,
    rowIds: query.rowIds,
    periods: query.periods,
    units: query.availabilityQuery.units,
    reservationCheckUnits: query.reservationCheckQuery.units,
  });

  return {
    preferences: query.preferences,
    effectiveShowAvailability: query.effectiveShowAvailability,
    hasCampaignDates: query.hasCampaignDates,
    availabilityQuery: query.availabilityQuery,
    periods: query.periods,
    rowIds: query.rowIds,
    mediaUnitIdById: query.mediaUnitIdById,
    occupancyByRowId: grids.occupancyByRowId,
    entriesGrid: grids.entriesGrid,
    reservedUnitIds: grids.reservedUnitIds,
    gridRows: grids.gridRows,
    grid: grids.grid,
  };
}
